import { useEffect, useRef, useState } from 'react'
import "pannellum/build/pannellum.css";
import { Container, PanoramaWrapper, PseudoFullscreenButton, RecenterButton, Title, ViewerContainer } from 'components/Panorama360/styles'
import { CgMaximize } from "react-icons/cg";
import { CgMinimize } from "react-icons/cg";

type Panorama360Variant = 'standalone' | 'carousel' | 'lightbox';

interface Panorama360Props {
  part: any;
  variant?: Panorama360Variant;
}

const isIOSPhone = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /iPhone/i.test(navigator.userAgent);
};

const Panorama360 = ({ part, variant = 'standalone' }: Panorama360Props) => {
    const inLightbox = variant === 'lightbox';
    const fillContainer = inLightbox;
    const viewerRef = useRef(null);
    const wrapperRef = useRef(null);
    const viewerInstance = useRef(null);
    const orientationActive = useRef(false);
    const [isPseudoFullscreen, setIsPseudoFullscreen] = useState<boolean>(false);
    const [nativeFullscreenSupported, setNativeFullscreenSupported] = useState<boolean>(true);

  useEffect(() => {
    const fullscreen: boolean = document.fullscreenEnabled && !isIOSPhone();
    setNativeFullscreenSupported(fullscreen);
    import("pannellum").then(() => {
      if (window.pannellum && viewerRef.current && part.url) {
        const commonOptions = {
          type: 'equirectangular',
          panorama: part.url,
          autoLoad: true,
          ignoreGPanoXMP: true,
          pitch: 0,
          yaw: 0,
          hfov: 75,
          draggable: true,
          mouseZoom: true,
          showControls: true,
          orientationOnByDefault: !!(typeof DeviceMotionEvent),
          showFullscreenCtrl: fullscreen,
        };

          if (!part.half) {
            viewerInstance.current = window.pannellum.viewer(viewerRef.current, {
              ...commonOptions,
              minPitch: part.tiltDown ? -parseInt(part.tiltDown) : -85,
              maxPitch: part.tiltUp ? -parseInt(part.tiltUp) : 85,
            });
          } else {
            viewerInstance.current = window.pannellum.viewer(viewerRef.current, {
              ...commonOptions,
              minPitch: -55,
              maxPitch: 55,
              minYaw: -90,
              maxYaw: 90,
            });
          }
        }
      });

      return () => {
        if (viewerInstance.current && viewerInstance.current.destroy) {
          viewerInstance.current.destroy();
          orientationActive.current = false;
        }
      };
    }, [part.url, part.title, part.tiltUp, part.tiltDown, ]);

    useEffect(() => {
      viewerInstance.current?.resize?.();

      if (!isPseudoFullscreen) {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      const patchedAncestors = [];
      let ancestor = wrapperRef.current?.parentElement;
      while (ancestor && ancestor !== document.body) {
        const computed = window.getComputedStyle(ancestor);
        if (computed.transform !== 'none' || computed.filter !== 'none' || computed.perspective !== 'none') {
          patchedAncestors.push({
            element: ancestor,
            transform: ancestor.style.transform,
            filter: ancestor.style.filter,
            perspective: ancestor.style.perspective,
          });
          ancestor.style.transform = 'none';
          ancestor.style.filter = 'none';
          ancestor.style.perspective = 'none';
        }
        ancestor = ancestor.parentElement;
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsPseudoFullscreen(false);
        }
      };
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = previousOverflow;
        patchedAncestors.forEach(({ element, transform, filter, perspective }) => {
          element.style.transform = transform;
          element.style.filter = filter;
          element.style.perspective = perspective;
        });
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isPseudoFullscreen]);

    const handleRecenter = () => {
      if (viewerInstance.current) {
        viewerInstance.current.lookAt(0, 0, 75, 500);
      }
    };

    const handleTogglePseudoFullscreen = () => {
      setIsPseudoFullscreen((prev) => !prev);
    };

    return (
      <Container $fill={fillContainer}>
        {part.title && !fillContainer && <Title>{part.title}</Title>}
        <PanoramaWrapper ref={wrapperRef} $pseudoFullscreen={isPseudoFullscreen} $fill={fillContainer}>
          <ViewerContainer ref={viewerRef} $pseudoFullscreen={isPseudoFullscreen} $fill={fillContainer} />

          <RecenterButton onClick={handleRecenter} aria-label="Recentrer" title="Recentrer la vue">
            ↻
          </RecenterButton>

          {!nativeFullscreenSupported && !inLightbox && (
            <PseudoFullscreenButton
              onClick={handleTogglePseudoFullscreen}
              aria-label={isPseudoFullscreen ? 'Quitter le mode plein écran' : 'Plein écran'}
              title={isPseudoFullscreen ? 'Quitter le mode plein écran' : 'Plein écran'}
            >
              {isPseudoFullscreen ? <CgMinimize /> : <CgMaximize />}
            </PseudoFullscreenButton>
          )}
        </PanoramaWrapper>
        {part.legend && part.legend.length > 0 && !fillContainer && (
          <div style={{ marginBottom: '20px' }}>
            <legend style={{fontSize: part.legendSize ? part.legendSize === 'small' ? "13px" : "16px" : '13px'}}>{part.legend}</legend>
          </div>
        )}
      </Container>
    )
};

Panorama360.displayName = 'Panorama360';

export default Panorama360;